import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { threeDSecureVerifyCard } from '../../Utility/BrainTree'

import './ButtonGooglePay.scss'
import {dataLayerPushError} from "../../Utility/WoodGreenPaymentsApi";

/**
 * ButtonPaypal component.
 */
export const ButtonGooglePay = ({ brainTreeInstancesGet, brainTreeIsInstantiated, environment, orderUpdate, onSetError, onSetProcessing, paymentHasCompleted, values, onPaymentSelect, donationFeeRate }) => {
  const [ready, readySet] = useState(false)
  const [paymentsClient, paymentsClientSet] = useState(null)
  const container = useRef(null)

  const handlePayment = () => {
    const brainTreeInstances = brainTreeInstancesGet()

    const totalPrice = (donationFeeRate && values.cover_donation_fee === 'Y')
      ? (parseFloat(values.payment_amount) * donationFeeRate).toFixed(2).replace('.00', '')
      : values.payment_amount

    const paymentDataRequest = brainTreeInstances.googlePayCheckoutInstance.createPaymentDataRequest({
      transactionInfo: {
        currencyCode    : 'GBP',
        totalPriceStatus: 'FINAL',
        totalPrice      : totalPrice.toString()
      }
    });

    // We recommend collecting billing address information, at minimum
    // billing postal code, and passing that billing postal code with all
    // Google Pay card transactions as a best practice.
    // See all available options at https://developers.google.com/pay/api/web/reference/object
    const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0]
    cardPaymentMethod.parameters.billingAddressRequired   = true
    cardPaymentMethod.parameters.billingAddressParameters = {
      format             : 'FULL',
      phoneNumberRequired: true
    }

    paymentsClient.loadPaymentData(paymentDataRequest)
      .then(paymentData => {
        brainTreeInstances.googlePayCheckoutInstance.parseResponse(paymentData, (error, payload) => {
          if (error) {
            console.log('Error', error)
            return true
          }

          let payloadValues = {
            payment_amount: totalPrice
          }

          if (paymentData['paymentMethodData']['info']['billingAddress']) {
            payloadValues['telephone_number']         = paymentData['paymentMethodData']['info']['billingAddress']['phoneNumber']
            payloadValues['billing_address_line_1']   = paymentData['paymentMethodData']['info']['billingAddress']['address1']
            payloadValues['billing_address_line_2']   = paymentData['paymentMethodData']['info']['billingAddress']['address2']
            payloadValues['billing_address_town_city'] = paymentData['paymentMethodData']['info']['billingAddress']['locality']
            payloadValues['billing_address_post_code'] = paymentData['paymentMethodData']['info']['billingAddress']['postalCode']
          }

          if (payload['details']['isNetworkTokenized'] === false) {
            brainTreeInstances.threeDSecure.verifyCard(threeDSecureVerifyCard(payload, payloadValues, donationFeeRate))
              .then(payload => {
                if (payload['threeDSecureInfo']['liabilityShifted']) {
                  onPaymentSelect(true)
                  orderUpdateFromPayloadValues(payload, payloadValues, brainTreeInstances.dataCollector['deviceData'])
                }
                else {
                  throw {
                    message: 'Try a different card. Your bank can’t verify your information. You should also disable any pop-up blockers you have running.'
                  }
                }
              })
            .catch(err => {
              throw err
            })
          }
          else {
            onPaymentSelect(true)
            orderUpdateFromPayloadValues(payload, payloadValues, brainTreeInstances.dataCollector['deviceData'])
          }
        })
      })
      .catch(error => {
        console.log('Error', error)
      })
  }

  /**
   * Update the order values with the given payload response.
   *
   * @param {{}} payload
   *   The response payload
   * @param {{}} payloadValues
   *   The payload values (amount, billing address, etc ...)
   * @param {{}} dataCollector
   *   The data collection data.
   */
  const orderUpdateFromPayloadValues = (payload, payloadValues, dataCollector) => {
    orderUpdate({
      // first_name               : event.payment['billingContact']['givenName'] ? event.payment['billingContact']['givenName'] : '',
      // last_name                : event.payment['billingContact']['familyName'] ? event.payment['billingContact']['familyName'] : '',
      contact_address_line_1   : payloadValues['billing_address_line_1']    ? payloadValues['billing_address_line_1'] : '',
      contact_address_line_2   : payloadValues['billing_address_line_2']    ? payloadValues['billing_address_line_2'] : '',
      contact_address_town_city: payloadValues['billing_address_town_city'] ? payloadValues['billing_address_town_city'] : '',
      contact_address_post_code: payloadValues['billing_address_post_code'] ? payloadValues['billing_address_post_code'] : '',
      telephone_number         : payloadValues['telephone_number']          ? payloadValues['telephone_number'] : '',
      payment_client_data      : dataCollector,
      payment_nonce            : payload.nonce,
      payment_provider         : 'Braintree',
      payment_method           : 'GooglePay'
    })
  }

  useEffect(() => {
    if (paymentsClient) {
      const brainTreeInstances = brainTreeInstancesGet()

      paymentsClient.isReadyToPay({
          apiVersion                   : 2,
          apiVersionMinor              : 0,
          allowedPaymentMethods        : brainTreeInstances.googlePayCheckoutInstance.createPaymentDataRequest().allowedPaymentMethods,
          existingPaymentMethodRequired: true
        })
        .then(response => {
          if (response.result) {
            const button = paymentsClient.createButton({
              buttonColor   : 'default',
              buttonType    : 'donate',
              buttonSizeMode: 'fill',
              onClick       : handlePayment,
            })

            container.current.innerHTML = ""
            container.current.append(button)

            readySet(true)
          }
        })
        .catch(error => {
          console.error('error', error)
        })
      }
    }, [paymentsClient, values.cover_donation_fee, values.payment_amount])


  console.log('environment', environment)

  useEffect(() => {
    if (brainTreeIsInstantiated) {
      const paymentsClient = new google.payments.api.PaymentsClient({
        environment: environment // Or 'PRODUCTION'
      })

      paymentsClientSet(paymentsClient)
    }
  }, [brainTreeIsInstantiated])

  return (
    <div ref={container} className={'m-button-google-pay'} />
  )
}

/**
 * ButtonPaypal PropTypes.
 */
ButtonGooglePay.propTypes = {
  brainTreeIsInstantiated: PropTypes.bool,
  brainTreeInstancesGet  : PropTypes.func,
  paymentHasCompleted    : PropTypes.bool,
  environment            : PropTypes.string,
  onSetProcessing        : PropTypes.func,
  onSetError             : PropTypes.func,
  orderUpdate            : PropTypes.func,
  values: PropTypes.shape({
    payment_amount: PropTypes.string,
    cover_donation_fee: PropTypes.string,
  }),
  onPaymentSelect        : PropTypes.func,
  donationFeeRate        : PropTypes.number
}

/**
 * ButtonPaypal PropTypes.
 */
ButtonGooglePay.defaultProps = {
  environment: 'TEST'
}
